/*---------------------------------------------------------------------------------------------
 * Copyright (c) Bentley Systems, Incorporated. All rights reserved.
 * See LICENSE.md in the project root for license terms and full copyright notice.
 *--------------------------------------------------------------------------------------------*/
/** @packageDocumentation
 * @module AccessControlClient
 */

import type { AccessToken } from "@itwin/core-bentley";
import type { IGroupsClient } from "../accessControlClientInterfaces/GroupClient";
import type { BentleyAPIResponse, ODataQueryParams } from "../types/CommonApiTypes";
import type { Group, GroupsResponseWithConditionalLinks, SingleGroupResponse } from "../types/Groups";
import { BaseClient } from "./BaseClient";

/** Client API to perform iTwin group operations.
 */
export class GroupsClient extends BaseClient implements IGroupsClient{
  /** Create a new GroupsClient instance
   * @param url Optional base URL for the access control service. If not provided, defaults to base url.
   */
  public constructor(url?: string) {
    super(url);
  }

  /** Retrieves a list of available user roles that are defined for a specified iTwin
    * @param accessToken The client access token string
    * @param iTwinId The id of the iTwin
    * @returns MultipleGroupsResponse
    */
  public async getITwinGroups<T extends Pick<ODataQueryParams, "top" | "skip"> | undefined = undefined>(
    accessToken: AccessToken,
    iTwinId: string,
    arg?: T
  ): Promise<BentleyAPIResponse<GroupsResponseWithConditionalLinks<T>>>{
    let url = `${this._baseUrl}/${iTwinId}/groups`;

    if (arg) {
      url += `?${this.getQueryString(GroupsClient.paginationParamMapping, { top: arg.top, skip: arg.skip })}`;
    }
    return this.sendGenericAPIRequest(accessToken, "GET", url);
  }

  /** Retrieves the specified role for the specified iTwin
    * @param accessToken The client access token string
    * @param iTwinId The id of the iTwin
    * @returns Group
    */
  public async getITwinGroup(
    accessToken: AccessToken,
    iTwinId: string,
    groupId: string,
  ): Promise<BentleyAPIResponse<SingleGroupResponse>>{
    const url = `${this._baseUrl}/${iTwinId}/groups/${groupId}`;
    return this.sendGenericAPIRequest(accessToken, "GET", url);
  }

  /** Creates a new iTwin group
    * @param accessToken The client access token string
    * @param iTwinId The id of the iTwin
    * @param group The group to be created
    * @returns Group
    */
  public async createITwinGroup(
    accessToken: AccessToken,
    iTwinId: string,
    group: Pick< Group, "name" | "description">
  ): Promise<BentleyAPIResponse<SingleGroupResponse>>{
    const url = `${this._baseUrl}/${iTwinId}/groups`;
    return this.sendGenericAPIRequest(accessToken, "POST", url, group);
  }

  /** Delete the specified iTwin group
    * @param accessToken The client access token string
    * @param iTwinId The id of the iTwin
    * @param groupId The id of the group to remove
    * @returns No Content
    */
  public async deleteITwinGroup(
    accessToken: AccessToken,
    iTwinId: string,
    groupId: string,
  ): Promise<BentleyAPIResponse<undefined>>{
    const url = `${this._baseUrl}/${iTwinId}/groups/${groupId}`;
    return this.sendGenericAPIRequest(accessToken, "DELETE", url);
  }

  /** Update the specified iTwin group
    * @param accessToken The client access token string
    * @param iTwinId The id of the iTwin
    * @param groupId The id of the role to update
    * @param group The updated group
    * @returns Group that was updated
    */
  public async updateITwinGroup(
    accessToken: AccessToken,
    iTwinId: string,
    groupId: string,
    group: Partial<Pick<Group, "name" | "description"> & { members: string[]; imsGroups: string[] }>
  ): Promise<BentleyAPIResponse<SingleGroupResponse>>{
    const url = `${this._baseUrl}/${iTwinId}/groups/${groupId}`;
    return this.sendGenericAPIRequest(accessToken, "PATCH", url, group);
  }
}
